import { orderBy } from "lodash-es";

import type { AppStore } from "@/store/AppStore";
import { isMemAccount } from "@/store/contacts/isMemAccount";
import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models/audio-recording";
import { audioRecordingModule } from "@/modules/audio-recording";
import { clientEnvModule } from "@/modules/client-env";
import type { AudioLevelProbe } from "@/modules/audio-recording/legacy";
import {
  AudioRecordingObservable,
  AudioRecordingProcessingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings";
import { RecordingStatus } from "@/modules/audio-recording/legacy";
import { TranscriptionStatus } from "@/store/audio/recording";
import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds";
import type {
  AudioCaptureEnvironmentState,
  AudioCaptureMicrophoneState,
  AudioCaptureRecordingState,
  AudioCaptureTranscriptEntry,
  AudioCaptureTranscriptState,
} from "./audio-capture-actions.types";
import { canPressDone, isFinishedRecording, isProcessing } from "./audio-capture-actions.utils";

export interface AudioCaptureSourceSnapshot {
  recording: Omit<AudioCaptureRecordingState, "waveform">;
  ui: {
    showOnboardingTooltip: boolean;
    isAutoStopModalVisible: boolean;
    shouldShowDesktopDownloadNotice: boolean;
  };
  environment: AudioCaptureEnvironmentState;
  visualizer: AudioLevelProbe | null;
  recordingId: string | null;
}

export function normalizeTranscriptEntries(
  turns: LocalAudioRecordingTranscriptTurnRecord[]
): AudioCaptureTranscriptEntry[] {
  const sortedTurns = orderBy(turns, turn => turn.transcriptTurnIndex, "asc");

  return sortedTurns.map((turn, index) => {
    const text = (turn.content?.words ?? []).map(word => word.text).join(" ");
    const nextTurn = sortedTurns[index + 1];
    const channel = typeof turn.channel === "number" ? turn.channel : 0;
    const nextChannel =
      typeof nextTurn?.channel === "number" ? (nextTurn?.channel as number) : null;

    return {
      id: turn.id,
      alignment:
        channel < 0
          ? MDSBubbleAlignment.Center
          : channel > 0
            ? MDSBubbleAlignment.Left
            : MDSBubbleAlignment.Right,
      variant:
        channel < 0
          ? MDSBubbleVariant.Transparent
          : channel === 0
            ? MDSBubbleVariant.VoiceSelf
            : MDSBubbleVariant.VoiceOthers,
      text,
      isLastInGroup: channel !== nextChannel,
    } satisfies AudioCaptureTranscriptEntry;
  });
}

const legacyTranscriptLabel = (channel: number): string =>
  channel === 0 ? "Me" : "Other Speakers";

const buildFullTranscript = (turns: LocalAudioRecordingTranscriptTurnRecord[]): string => {
  if (!turns.length) return "";

  const collapsed = audioRecordingModule.collapseRecorderTranscriptTurns(turns);
  return collapsed
    .map(turn => {
      const words = (turn.content?.words ?? []).map(word => word.text).join(" ");
      return `[${legacyTranscriptLabel(turn.channel)}]: ${words}`;
    })
    .join("\n");
};

const deriveRecordingStatus = ({
  recording,
  isRecordingPaused,
  isInitializing,
  isUploaderActuallyRunning,
  isTransformerActivelyRunning,
}: {
  recording: AudioRecordingObservable | undefined;
  isRecordingPaused: boolean;
  isInitializing: boolean;
  isUploaderActuallyRunning?: boolean;
  isTransformerActivelyRunning?: boolean;
}): RecordingStatus => {
  if (isInitializing) {
    return RecordingStatus.INITIALIZING;
  }

  if (!recording) {
    return RecordingStatus.INITIALIZING;
  }

  if (!recording.hasStartedRecording) {
    return RecordingStatus.INITIALIZING;
  }

  if (recording.isRecording) {
    if (isRecordingPaused) {
      return RecordingStatus.PAUSED;
    }

    return RecordingStatus.RECORDING;
  }

  // Only show UPLOADING status if upload is actually running
  if (isUploaderActuallyRunning === true) {
    return RecordingStatus.UPLOADING;
  }

  // Only show PROCESSING status if transform is actually running
  if (isTransformerActivelyRunning === true) {
    return RecordingStatus.PROCESSING;
  }

  return RecordingStatus.STOPPED;
};

export function createAudioCaptureSourceSnapshot(
  store: AppStore,
  { noteId }: { noteId: string }
): AudioCaptureSourceSnapshot {
  const audioStore = store.audio;
  const recordingStore = audioStore.recording;
  const uploadStore = audioStore.uploading;
  const transformStore = audioStore.transforming;

  const noteRecording = store.audioRecordings.getNoteRecording({ noteId });
  const recordingObservable = noteRecording.recording as AudioRecordingObservable | undefined;
  const recordingId = recordingObservable?.id ?? null;

  const transcriptTurns = recordingObservable?.transcriptTurns ?? [];
  const transcriptEntries = normalizeTranscriptEntries(transcriptTurns);

  const transcript: AudioCaptureTranscriptState = {
    entries: transcriptEntries,
    fullText: buildFullTranscript(transcriptTurns),
    hasTranscript: transcriptEntries.length > 0,
    isDisconnected: recordingId
      ? recordingStore.getTranscriptionStatus(recordingId) === TranscriptionStatus.Disconnected
      : false,
    isCompromised: recordingId
      ? recordingStore.getTranscriptionStatus(recordingId) === TranscriptionStatus.Compromised
      : false,
    isTranscriptionUnsupported: recordingStore.transcriptIsUnsupported,
  };

  const microphone: AudioCaptureMicrophoneState = {
    label: recordingStore.activeAudioInputDeviceLabel,
    isDisconnected: recordingStore.isMicrophoneDisconnected,
    isAudioSilent: recordingStore.isAudioSilent,
  };

  const uploadState = recordingId
    ? uploadStore.getProgress({ audioRecordingId: recordingId })
    : null;
  const transformState = recordingId
    ? transformStore.getTransformState({ audioRecordingId: recordingId })
    : null;

  // Check if upload/transform are actually running (not just database status)
  const isUploaderActuallyRunning = uploadState?.isUploading === true;
  const isTransformerActivelyRunning = transformState?.isTransforming === true;

  const uploadStatus = (() => {
    if (!recordingObservable) {
      return {
        state: "idle" as const,
        progressPercent: null,
        errorMessage: null,
      };
    }

    const progress =
      uploadState?.uploadProgressPercent ?? recordingObservable.uploadProgressPercent ?? null;
    const errorMessage =
      uploadState?.errorMessage ?? recordingObservable.uploadErrorMessage ?? null;
    const uploadStatusEnum = recordingObservable.uploadStatus;

    if (isUploaderActuallyRunning) {
      return {
        state: "running" as const,
        progressPercent: progress,
        errorMessage: null,
      };
    }

    if (errorMessage || uploadStatusEnum === AudioRecordingUploadStatus.ERROR) {
      return {
        state: "error" as const,
        progressPercent: progress,
        errorMessage: errorMessage ?? "Upload failed",
      };
    }

    // If database says IN_PROGRESS but upload isn't actually running, show error state for retry
    if (
      !isTransformerActivelyRunning &&
      uploadStatusEnum === AudioRecordingUploadStatus.IN_PROGRESS
    ) {
      return {
        state: "error" as const,
        progressPercent: progress,
        errorMessage: "Upload was interrupted. Please retry.",
      };
    }

    if (uploadStatusEnum === AudioRecordingUploadStatus.COMPLETED) {
      return {
        state: "completed" as const,
        progressPercent: progress,
        errorMessage: null,
      };
    }

    return {
      state: "idle" as const,
      progressPercent: progress,
      errorMessage: null,
    };
  })();

  const transformStatus = (() => {
    if (!recordingObservable) {
      return {
        state: "idle" as const,
        errorMessage: null,
      };
    }

    const errorMessage =
      transformState?.errorMessage ?? recordingObservable.transformErrorMessage ?? null;
    const processingStatusEnum = recordingObservable.processingStatus;

    // Only consider transform as "running" if it's actually running (has in-memory state)
    // If database says IN_PROGRESS but transform isn't actually running, treat as error/retry state
    if (isTransformerActivelyRunning) {
      return {
        state: "running" as const,
        errorMessage: null,
      };
    }

    if (errorMessage || processingStatusEnum === AudioRecordingProcessingStatus.ERROR) {
      return {
        state: "error" as const,
        errorMessage: errorMessage ?? "Processing failed",
      };
    }

    // If database says IN_PROGRESS but transform isn't actually running, show error state for retry
    if (
      !recordingObservable.isAwaitingTransformRemoteUpdate &&
      processingStatusEnum === AudioRecordingProcessingStatus.IN_PROGRESS
    ) {
      return {
        state: "error" as const,
        errorMessage: "Processing was interrupted. Please retry.",
      };
    }

    if (processingStatusEnum === AudioRecordingProcessingStatus.COMPLETED) {
      return {
        state: "completed" as const,
        errorMessage: null,
      };
    }

    return {
      state: "idle" as const,
      errorMessage: null,
    };
  })();

  const status = deriveRecordingStatus({
    recording: recordingObservable,
    isRecordingPaused: recordingStore.isPaused,
    isInitializing: recordingStore.isInitializing,
    isUploaderActuallyRunning,
    isTransformerActivelyRunning,
  });

  const noteObservable = store.notes.getNoteObservableById({ noteId });
  const noteExistsRemotely =
    noteObservable && "existsRemotely" in noteObservable
      ? Boolean((noteObservable as { existsRemotely: boolean }).existsRemotely)
      : false;

  const isUploading = uploadStatus.state === "running";

  const recordingBase: Omit<AudioCaptureRecordingState, "waveform"> = {
    status,
    durationLabel: recordingObservable?.formattedDuration ?? "00:00",
    uploadProgressPercent: uploadStatus.progressPercent,
    isInitializing: recordingStore.isInitializing,
    transcript,
    microphone,
    shouldDisplayRetryLabel: Boolean(
      uploadStatus.state === "error" || transformStatus.state === "error"
    ),
    canProcessRecording: canPressDone({
      status,
      isMicrophoneDisconnected: recordingStore.isMicrophoneDisconnected,
      noteExistsRemotely,
    }),
    isProcessing:
      uploadStatus.state === "running" ||
      transformStatus.state === "running" ||
      isProcessing(status),
    isUploading,
    isFinished:
      transformStatus.state === "completed" ||
      recordingObservable?.hasFinishedProcessing ||
      isFinishedRecording(status),
    activeMicrophoneLabel: recordingStore.activeAudioInputDeviceLabel,
    noteExistsRemotely,
    uploadStatus,
    transformStatus,
  };

  const ui = {
    showOnboardingTooltip: audioStore.ui.showOnboardingTooltip,
    isAutoStopModalVisible: audioStore.ui.autoStopModalVisible,
    shouldShowDesktopDownloadNotice: !clientEnvModule.isDesktop(),
  };

  const environment: AudioCaptureEnvironmentState = {
    isAdmin: isMemAccount(store.account.myAccount),
    isDesktop: store.interface.isDesktop,
  };

  return {
    recording: recordingBase,
    ui,
    environment,
    visualizer: recordingStore.visualizer,
    recordingId,
  };
}
